import type { AcpPlanData, AcpSubagentData } from '@crowdex/agent-contracts';
import { toast } from '@crowdex/ui/components/toaster';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type { PullRequestCockpit as PullRequestCockpitData } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { pullRequestIssueText } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { Globe, Monitor } from 'lucide-react';
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { type AgentTerminalDisplay, PanelEmpty } from '@/components/desktop-panel-primitives';
import { AgentTerminalPane } from '@/components/desktop-side-panel/agent-terminal-pane';
import { CloudBrowserView } from '@/components/desktop-side-panel/cloud-browser-view';
import { ComputerView } from '@/components/desktop-side-panel/computer-view';
import { DesktopDisplayView } from '@/components/desktop-side-panel/desktop-display-view';
import { FileSelectionToolbar } from '@/components/desktop-side-panel/file-selection-toolbar';
import { FilesView } from '@/components/desktop-side-panel/files-view';
import { NewTabView } from '@/components/desktop-side-panel/new-tab-view';
import {
  PanelSplitFrame,
  type PanelSplitRect,
} from '@/components/desktop-side-panel/panel-split-frame';
import { PanelTabBar } from '@/components/desktop-side-panel/panel-tab-bar';
import { PanelTabList } from '@/components/desktop-side-panel/panel-tab-list';
import { PlacedTab } from '@/components/desktop-side-panel/placed-tab';
import { PlanView } from '@/components/desktop-side-panel/plan-view';
import { LocalPortsView, PortsView } from '@/components/desktop-side-panel/ports-view';
import { ReviewView, reviewFileCount } from '@/components/desktop-side-panel/review-view';
import { usePanelItems } from '@/components/desktop-side-panel/use-panel-items';
import { WorkspaceUnavailable } from '@/components/desktop-side-panel/workspace-unavailable';
import { DesktopSubagentConversation } from '@/components/desktop-subagent-conversation';
import type { BrowserSelection } from '@/components/native-browser-view';
import { SideChatPanel } from '@/components/side-chat-panel';
import { insertInComposer } from '@/features/apps/agent-ask';
import { AppArtifactsSection } from '@/features/apps/app-artifacts-section';
import { ArtifactItemView } from '@/features/apps/artifact-item-view';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import type { NativePluginContext } from '@/features/plugins/native-plugin';
import { NativePluginPanel, usesNativePluginPanel } from '@/features/plugins/native-plugin-panel';
import {
  type SurfacePlacements,
  terminalSurface,
  webSurface,
} from '@/features/plugins/platform-services';
import { PluginHostServices } from '@/features/plugins/plugin-services';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import type { PullRequestTextGenerator } from '@/features/pull-request/create-pull-request-dialog';
import { PullRequestPanel } from '@/features/pull-request/pull-request-panel';
import { resolveBrowserInput } from '@/lib/browser-context';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { CloudConversationStartupPhase } from '@/lib/conversations/cloud-conversation-startup';
import type {
  ConversationDockSide,
  ConversationLayoutNode,
} from '@/lib/conversations/conversation-layout';
import { PREVIEW_FILES_CLIENT, PREVIEW_LOCAL_PORTS } from '@/lib/desktop-preview-files';
import { panelTabListSlot, panelTitleSlot } from '@/lib/desktop-shell/panel-screen-slots';
import { panelSplitTabs } from '@/lib/desktop-shell/panel-split';
import { CONVERSATIONS_SIDE, useSidebarSide } from '@/lib/desktop-shell/sidebar-side';
import type { PendingAnnotation, StateSetter } from '@/lib/desktop-shell/store';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { isElectronDesktop } from '@/lib/electron-bridge';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import { type NativeAgent, openNativeWorkspaceIn } from '@/lib/native-desktop';
import { useRouter } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import {
  type BrowserSessionMode,
  browserSessionDisplayName,
  withBrowserSessionMode,
} from '@/lib/side-panel/browser-session';
import {
  buildMentionTargets,
  type MentionTarget,
  mentionTargetsEqual,
  offeredArtifact,
} from '@/lib/side-panel/mention-targets';
import {
  describePanelTabs,
  groupViewTabs,
  type PanelInstance,
  type PanelTabSession,
} from '@/lib/side-panel/panel-instance';
import {
  clampKeyboardResize,
  clampPointerResize,
  DOCKED_PANEL_MAX_WIDTH,
  PANEL_MAIN_MIN_PIXELS,
  PANEL_MIN_PIXELS,
} from '@/lib/side-panel/panel-resize';
import {
  activatePanelTab,
  activePanelView,
  lastPanelTabOf,
  type PanelTab,
  type PanelTabs,
  retainPanelTabs,
  showPanelView,
  surfaceSlotClass,
} from '@/lib/side-panel/panel-tabs';
import { type PanelToolEntry, panelToolEntries } from '@/lib/side-panel/panel-tools';
import { beginPointerResize, layoutContainer } from '@/lib/side-panel/pointer-resize';
import { SIDE_PANEL_ITEMS, type SidePanelView } from '@/lib/side-panel/side-panel-items';
import { usePanelSplitPreview } from '@/lib/side-panel/split-drag';
import { useBrowserTabs } from '@/lib/side-panel/use-browser-tabs';
import { useCloudWorkspaceClient } from '@/lib/side-panel/use-cloud-workspace-client';
import { useLocalPorts } from '@/lib/side-panel/use-local-ports';
import { useLocalWorkspaceClient } from '@/lib/side-panel/use-local-workspace-client';
import type { PanelTabSplit } from '@/lib/side-panel/use-panel-tabs';
import { usePanelTabs } from '@/lib/side-panel/use-panel-tabs';
import { usePullRequestCockpit } from '@/lib/side-panel/use-pull-request-cockpit';
import { useSandboxPorts } from '@/lib/side-panel/use-sandbox-ports';
import { useTerminalTabs } from '@/lib/side-panel/use-terminal-tabs';
import { useWorkspaceFileExplorer } from '@/lib/side-panel/use-workspace-file-explorer';
import { OPEN_WORKSPACE_FILE_EVENT } from '@/lib/side-panel/workspace-file-index';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/** Where a view or a tab shows in the panel: its classes, and over its cell of the split. */
type TabSlot = {
  className: string;
  style?: CSSProperties;
  'data-panel-split-target'?: string;
  onPointerDownCapture?: () => void;
};

/** The share of the window Review takes at least (D24): it lists the files beside the diff. */
const REVIEW_PANEL_SIZE = 62;

export { backgroundOutputFile } from '@/lib/desktop-panel-helpers';
export type { MentionTarget } from '@/lib/side-panel/mention-targets';
export type { SidePanelView } from '@/lib/side-panel/side-panel-items';

const UUID = /^[0-9a-f-]{36}$/i;

export function SidePanel({
  tabs,
  onTabsChange,
  retainedTabIds,
  split,
  onSplitTab,
  onUnsplitTab,
  agent,
  pluginPermissions,
  cloudRuntime,
  cloudWorkspaceError,
  cloudWorkspacePhase,
  cloudWorkspaceStarting,
  pluginConversation,
  hidden,
  manualResize,
  presentation = 'docked',
  maximized,
  onClose,
  onSizeChange,
  onToggleMaximize,
  onSelectView,
  onReveal,
  onBrowserSelection,
  onMentionTargetsChange,
  onAnnotateSelection,
  onAskSelectionInSideChat,
  onMoreDetailsSelection,
  onSubmitReview,
  onPullRequestChange,
  plan,
  pullRequest,
  currentBranch,
  pullRequestCockpitOverride,
  reviewDiffOverride,
  selectedSubagent,
  agentTerminals,
  selectedAgentTerminalId,
  onAgentTerminalRequestConsumed,
  onKillAgentTerminal,
  onAgentTerminalStopped,
  sideChatPrompt,
  taskId,
  size,
  terminalAction,
  onTerminalActionConsumed,
  workspace,
  resolveReference,
  generateGitText,
}: {
  /** Writes a pull request's title and description with the session's model. */
  generateGitText?: PullRequestTextGenerator | undefined;
  /** The open tabs and the one shown. */
  tabs: PanelTabs;
  onTabsChange: StateSetter<PanelTabs>;
  /** Tabs open in other conversations' panels, whose sessions are kept for them. */
  retainedTabIds?: ReadonlySet<string>;
  /** The tabs shown side by side; null shows the active tab alone. */
  split: ConversationLayoutNode | null;
  /** Shows the tab `tabId` beside the tab `targetId`, on `side`. */
  onSplitTab: (tabId: string, targetId: string, side: ConversationDockSide) => void;
  /** Takes a tab out of the split. */
  onUnsplitTab: (tabId: string) => void;
  agent: NativeAgent | null;
  pluginPermissions: ReadonlySet<string>;
  cloudRuntime: CloudRuntime | null;
  cloudWorkspaceError: string | null;
  cloudWorkspacePhase: CloudConversationStartupPhase | null;
  cloudWorkspaceStarting: boolean;
  pluginConversation: NativePluginContext['conversation'];
  hidden: boolean;
  manualResize: boolean;
  /**
   * `screen`: a screen of the phone app, under its own navigation (the conversation's tabs):
   * its tab strip only when it holds tools, without the controls of a docked panel.
   */
  presentation?: 'docked' | 'screen';
  maximized: boolean;
  onClose: () => void;
  onSizeChange: (size: number) => void;
  onToggleMaximize: () => void;
  /** Shows a view: its last tab, or a new one. */
  onSelectView: (view: SidePanelView) => void;
  /** Opens the panel on what it shows. */
  onReveal: () => void;
  onBrowserSelection: (selection: BrowserSelection) => void;
  onMentionTargetsChange: (targets: readonly MentionTarget[]) => void;
  onAnnotateSelection: (selection: PendingAnnotation) => void;
  onAskSelectionInSideChat: (content: string) => void;
  onMoreDetailsSelection: (content: string) => void;
  onSubmitReview: (review: string) => void;
  onPullRequestChange: () => Promise<void>;
  plan: AcpPlanData | null;
  pullRequest: PullRequestSummary | null;
  currentBranch: string | null;
  pullRequestCockpitOverride?: PullRequestCockpitData | null;
  reviewDiffOverride: string | null;
  selectedSubagent: AcpSubagentData | null;
  agentTerminals: readonly AgentTerminalDisplay[];
  selectedAgentTerminalId: string | null;
  onAgentTerminalRequestConsumed: () => void;
  onKillAgentTerminal: (terminalId: string, outputFile?: string) => Promise<void>;
  onAgentTerminalStopped: (terminalId: string) => void;
  sideChatPrompt: { autoSubmit: boolean; id: string; text: string } | null;
  taskId: string;
  size: number;
  terminalAction: {
    id: string;
    name: string;
    command: string;
    env: Record<string, string>;
  } | null;
  onTerminalActionConsumed: () => void;
  workspace: string | null;
  resolveReference: (kind: MentionTarget['kind'], id: string) => Promise<unknown>;
}) {
  const workbench = useWorkbench();
  const router = useRouter();
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  // The user's editor (the preferred one, else the first): Files opens a file in it.
  const editorOpener = useDesktopShellStore((state) => {
    const editors = state.workspaceOpeners.filter((opener) => opener.kind === 'ide');
    return editors.find((opener) => opener.id === state.preferredOpenerId) ?? editors[0];
  });
  const webSessionPanel = workbench.panels.find((panel) => panel.session?.surface === 'web');
  const terminalSessionPanel = workbench.panels.find(
    (panel) => panel.session?.surface === 'terminal',
  );
  const webSessionView = webSessionPanel?.view;
  const terminalSessionView = terminalSessionPanel?.view;
  const activeView = activePanelView(tabs);
  // Review needs the width (D24): the panel widens (the shell folds the sidebar to its rail).
  const reviewing = !hidden && presentation === 'docked' && activeView === 'review';
  const widenForReview = useRef({ size, manualResize, onSizeChange });
  widenForReview.current = { size, manualResize, onSizeChange };
  useEffect(() => {
    const current = widenForReview.current;
    if (reviewing && current.manualResize && current.size < REVIEW_PANEL_SIZE)
      current.onSizeChange(REVIEW_PANEL_SIZE);
  }, [reviewing]);
  const openViews = useMemo(() => [...new Set(tabs.tabs.map((tab) => tab.view))], [tabs]);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [resizing, setResizing] = useState(false);
  const resizeCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => resizeCleanup.current?.(), []);
  const activeItem = SIDE_PANEL_ITEMS.find((item) => item.id === activeView);
  // A phone's panel is a screen: its active tab alone, its split kept for a wider window.
  const compact = presentation === 'screen';
  // Split, the panel shows the tabs of its split together while its active tab is one of them.
  const splitTabIds = compact ? [] : panelSplitTabs(split);
  const splitShown = tabs.activeId !== null && splitTabIds.includes(tabs.activeId);
  const shownTabs = tabs.tabs.filter((tab) =>
    splitShown ? splitTabIds.includes(tab.id) : tab.id === tabs.activeId,
  );
  const shown = (view: SidePanelView | null | undefined): boolean =>
    view !== null && view !== undefined && shownTabs.some((tab) => tab.view === view);
  const viewsRef = useRef<HTMLDivElement>(null);
  // A tab dragged over the view shown splits the panel there, beside it.
  const splitPreview = usePanelSplitPreview();
  const [splitRects, setSplitRects] = useState<Readonly<Record<string, PanelSplitRect>>>({});
  const reportSplitRect = useCallback((tabId: string, rect: PanelSplitRect) => {
    setSplitRects((current) => {
      const previous = current[tabId];
      return previous !== undefined &&
        previous.left === rect.left &&
        previous.top === rect.top &&
        previous.width === rect.width &&
        previous.height === rect.height
        ? current
        : { ...current, [tabId]: rect };
    });
  }, []);
  /** Where a tab shows: not at all (`undefined`), alone (`null`), or over its cell of the split. */
  function placementOf(tabId: string): PanelSplitRect | null | undefined {
    if (hidden || !shownTabs.some((tab) => tab.id === tabId)) return undefined;
    return splitShown ? splitRects[tabId] : null;
  }
  /** The placements of the tabs a surface shows (the browser's pages, the terminals). */
  function placementsOf(ids: readonly string[]): SurfacePlacements {
    return new Map(
      ids.flatMap((id) => {
        const placement = placementOf(id);
        return placement === undefined ? [] : [[id, placement] as const];
      }),
    );
  }
  const selectTab = (id: string) => onTabsChange((current) => activatePanelTab(current, id));
  // An app's tab is named by the item it shows.
  const [appTabTitles, setAppTabTitles] = useState<Readonly<Record<string, string>>>({});
  const renameAppTab = useCallback(
    (id: string) => (title: string | null) =>
      setAppTabTitles((current) => {
        if ((current[id] ?? null) === title) return current;
        const { [id]: _previous, ...rest } = current;
        return title === null ? rest : { ...rest, [id]: title };
      }),
    [],
  );
  const tabSplit: PanelTabSplit = {
    drop: (id, preview) => onSplitTab(id, preview.targetId, preview.side),
    // Dragged back onto the tab bar, a tab of the split shows alone again.
    unsplit: (id) => {
      if (splitTabIds.includes(id)) onUnsplitTab(id);
    },
  };
  const nativeReady = workspace !== null && isLocalMachine();
  // Outside Desktop and a local machine the browser would be a frame most sites refuse: a cloud
  // conversation's browser is its sandbox's.
  const sandboxBrowser = !isElectronDesktop() && !isLocalMachine();

  const cloudClient = useCloudWorkspaceClient(cloudRuntime, setFilesError);
  const localWorkspace = useLocalWorkspaceClient(
    isLocalMachine() && cloudRuntime === null ? workspace : null,
    setFilesError,
  );
  const localClient = localWorkspace.client;
  // One backend-agnostic frame client for every workspace op. `null` means the
  // selected Cloud runtime or mandatory local CLI is not ready yet.
  const client = useMemo<WorkspaceClient | null>(() => {
    if (cloudClient !== null) return cloudClient;
    if (localClient !== null) return localClient;
    return null;
  }, [cloudClient, localClient]);
  const workspaceReady = client !== null;
  // A cloud sandbox's ports, offered to forward to this machine as they open.
  // The preview shows the boards' data (its pull request, files and servers).
  const previewPanel = pullRequestCockpitOverride !== undefined;
  // On this computer, the workspace's own servers (D38).
  const localPorts = useLocalPorts(cloudRuntime === null ? localClient : null, shown('ports'));
  const sandboxPorts = useSandboxPorts({
    client: cloudClient,
    cloudRuntime,
    conversation: taskId,
  });
  const availablePluginViews = useMemo(
    () => (workbench.loaded ? new Set(workbench.panels.map((panel) => panel.view)) : undefined),
    [workbench.loaded, workbench.panels],
  );
  const { closeTab, tabDragProps, tabScrollRef } = usePanelTabs({
    availablePluginViews,
    onClose,
    setTabs: onTabsChange,
    split: tabSplit,
    tabs,
  });
  const {
    addTerminalTab,
    openAgentTerminals,
    renameTerminalSession,
    terminalSessions,
    terminalTabs,
  } = useTerminalTabs({
    agentTerminals,
    hidden,
    onAgentTerminalRequestConsumed,
    onTerminalActionConsumed,
    panelView: terminalSessionView,
    retainedTabIds,
    selectedAgentTerminalId,
    setTabs: onTabsChange,
    tabs,
    terminalAction,
    workspace,
  });
  // The terminal tabs that show an agent's command rather than a shell.
  const agentTerminalTabIds = new Set(openAgentTerminals.map((terminal) => terminal.id));
  const {
    activeBrowserId,
    addBrowserTab,
    browserSessions,
    controlledBrowserIds,
    updateBrowserSession,
  } = useBrowserTabs({
    // The embedded browser is part of the Electron shell, not of the local machine.
    nativeReady: nativeReady && isElectronDesktop(),
    onCloseTab: closeTab,
    onReveal,
    panelView: webSessionView,
    retainedTabIds,
    setTabs: onTabsChange,
    tabs,
    workspace,
  });
  const tabSessions = useMemo(
    () =>
      new Map<string, PanelTabSession>([
        ...browserSessions.map((session): [string, PanelTabSession] => [
          session.id,
          {
            name: browserSessionDisplayName(session),
            ...(session.favicon === undefined ? {} : { favicon: session.favicon }),
          },
        ]),
        ...terminalTabs.map((terminal): [string, PanelTabSession] => [
          terminal.id,
          { name: terminal.name },
        ]),
        ...Object.entries(appTabTitles).map(([id, name]): [string, PanelTabSession] => [
          id,
          { name },
        ]),
      ]),
    [appTabTitles, browserSessions, terminalTabs],
  );
  const panelItems = usePanelItems();
  const asideRef = useRef<HTMLElement>(null);
  const panelContentRef = useRef<HTMLDivElement>(null);
  // References must remain discoverable from the composer even when the
  // results panel itself is closed. A turn that ends may have changed them.
  const results = useArtifactIndex(UUID.test(taskId) ? taskId : null, {
    refreshKey: pluginConversation.busy,
  });
  // An artifact docked here that another conversation made is read from the account's index.
  const dockedElsewhere = openViews.some(
    (view) =>
      view.startsWith('artifact:') &&
      !results.artifacts.some((candidate) => `artifact:${candidate.id}` === view),
  );
  const accountArtifacts = useArtifactIndex(undefined, { enabled: dockedElsewhere });
  const files = useWorkspaceFileExplorer({
    active: shown('files'),
    // The preview's Files show the boards' repository (D34).
    client: client ?? (pullRequestCockpitOverride !== undefined ? PREVIEW_FILES_CLIENT : null),
    cloudRuntime,
    filesError,
    setFilesError,
    taskId,
    workspace,
  });
  const {
    cockpit: pullRequestCockpit,
    mutateCurrentPullRequest,
    reviewError,
    reviewSource,
    setReviewSource,
    summary: { diffText, reviewLabel, statusLines },
    updateGitFile,
  } = usePullRequestCockpit({
    activeView,
    client,
    cloudRuntime,
    onPullRequestChange,
    pullRequest,
    pullRequestCockpitOverride,
    reviewDiffOverride,
    taskId,
    workspace,
  });
  // The Review tab counts the files Review shows, from what it shows.
  const conversationFiles = useDesktopShellStore((state) => state.conversationFiles);
  const reviewScope = useDesktopShellStore((state) => state.reviewScope);
  const changedFiles = useMemo(
    () =>
      reviewFileCount({
        reviewDiffOverride,
        reviewError,
        reviewSource,
        statusLines,
        diffText,
        scope: reviewScope,
        files: conversationFiles,
      }),
    [
      reviewDiffOverride,
      reviewError,
      reviewSource,
      statusLines,
      diffText,
      reviewScope,
      conversationFiles,
    ],
  );
  const {
    activeFile,
    dismissFileSelection,
    expandedDirectories,
    fileEntries,
    fileFilter,
    fileSelection,
    loadingDirectories,
    openFile,
    refreshFiles,
    setFileFilter,
    setFileSelection,
    setShowFileSource,
    showFileSource,
    toggleDirectory,
  } = files;

  const mentionTargets = useMemo<readonly MentionTarget[]>(() => {
    return buildMentionTargets({
      artifacts: results.artifacts,
      openViews,
      // A tab is named as its tab bar names it: an app by its title, an artifact by its own.
      viewLabel: (view) =>
        view.startsWith('artifact:')
          ? (results.artifacts.find((artifact) => `artifact:${artifact.id}` === view)?.title ??
            offeredArtifact(view.slice('artifact:'.length))?.title)
          : panelItems.find((item) => item.id === view)?.label,
      sessionGroups: [
        ...(webSessionPanel?.session
          ? [
              {
                view: webSessionPanel.view,
                sessions: browserSessions,
                kind: webSessionPanel.session.mentionKind,
                detail: webSessionPanel.session.mentionDetail,
              },
            ]
          : []),
        ...(terminalSessionPanel?.session
          ? [
              {
                view: terminalSessionPanel.view,
                sessions: terminalSessions,
                kind: terminalSessionPanel.session.mentionKind,
                detail: terminalSessionPanel.session.mentionDetail,
              },
            ]
          : []),
      ],
    });
  }, [
    browserSessions,
    openViews,
    panelItems,
    results.artifacts,
    terminalSessionPanel,
    terminalSessions,
    webSessionPanel,
  ]);

  const publishedMentionTargets = useRef<{
    callback: typeof onMentionTargetsChange;
    targets: readonly MentionTarget[];
  } | null>(null);
  useEffect(() => {
    const published = publishedMentionTargets.current;
    if (
      published?.callback === onMentionTargetsChange &&
      mentionTargetsEqual(published.targets, mentionTargets)
    ) {
      return;
    }
    publishedMentionTargets.current = { callback: onMentionTargetsChange, targets: mentionTargets };
    onMentionTargetsChange(mentionTargets);
  }, [mentionTargets, onMentionTargetsChange]);

  useEffect(() => {
    // Opened with no tab: the launcher, to pick one. Unless a tab opened with it (a message's
    // terminal), which the tabs this render read do not hold yet.
    if (!hidden && activeView === null)
      onTabsChange((current) =>
        activePanelView(current) === null ? showPanelView(current, 'new-tab') : current,
      );
  }, [activeView, hidden, onTabsChange]);

  // Search & commands (D21) opens a file of the workspace in the Files tab.
  const openFileRef = useRef(files.openFile);
  openFileRef.current = files.openFile;
  useEffect(() => {
    const open = (event: Event) => {
      const path = (event as CustomEvent<unknown>).detail;
      if (typeof path !== 'string') return;
      onSelectView('files');
      void openFileRef.current(path);
    };
    window.addEventListener(OPEN_WORKSPACE_FILE_EVENT, open);
    return () => window.removeEventListener(OPEN_WORKSPACE_FILE_EVENT, open);
  }, [onSelectView]);

  const panelInstances = useMemo(
    () =>
      describePanelTabs({
        tabs,
        items: panelItems,
        artifacts: results.artifacts,
        sessions: tabSessions,
      }),
    [panelItems, results.artifacts, tabSessions, tabs],
  );

  // While the sidebar shows the docked app's side, an app (the browser too) lists its tabs'
  // items there: the strip shows it once.
  const [sidebarSide] = useSidebarSide(tabs.activeId);
  const appSideShown = sidebarSide !== CONVERSATIONS_SIDE;
  const stripInstances = useMemo(
    () =>
      appSideShown
        ? railPanels(workbench).reduce(
            (instances, panel) => groupViewTabs(instances, panel.view, panel.title),
            panelInstances,
          )
        : panelInstances,
    [appSideShown, panelInstances, workbench],
  );

  function openPanelInstance(panel: PanelInstance): void {
    onTabsChange((current) => activatePanelTab(current, panel.id));
  }

  function closePanelInstance(panel: PanelInstance): void {
    // An app's one tab (the browser's too, its tabs in the sidebar) closes all its tabs.
    if (!panel.grouped) {
      closeTab(panel.id);
      return;
    }
    const next = retainPanelTabs(tabs, (tab) => tab.view !== panel.type);
    onTabsChange(next);
    // Its last tabs closed, the panel closes, as `closeTab` does.
    if (next.tabs.length === 0) onClose();
  }

  /** Puts the tool picked from the "New tab" launcher in its place. */
  function replaceLauncher(open: () => void): void {
    onTabsChange((current) => retainPanelTabs(current, (tab) => tab.view !== 'new-tab'));
    open();
  }

  function startResize(event: ReactPointerEvent<HTMLHRElement>): void {
    event.preventDefault();
    const panel = event.currentTarget.parentElement;
    // The dock wraps the panel in a `display: contents` layer, which has no box to measure.
    const container = panel === null ? null : layoutContainer(panel);
    if (container === null) return;
    const bounds = container.getBoundingClientRect();
    if (bounds.width <= 0) return;
    resizeCleanup.current?.();

    function resize(pointerEvent: PointerEvent): void {
      const nextSize = ((bounds.right - pointerEvent.clientX) / bounds.width) * 100;
      onSizeChange(
        clampPointerResize({
          nextSize,
          dimension: bounds.width,
          minimumPixels: PANEL_MIN_PIXELS,
          mainMinimumPixels: PANEL_MAIN_MIN_PIXELS,
        }),
      );
    }

    setResizing(true);
    resizeCleanup.current = beginPointerResize({
      onResize: resize,
      onEnd: () => {
        resizeCleanup.current = null;
        setResizing(false);
      },
    });
  }

  function resizeWithKeyboard(event: ReactKeyboardEvent<HTMLDivElement>): void {
    const decrease = event.key === 'ArrowRight';
    const increase = event.key === 'ArrowLeft';
    if (!decrease && !increase) return;
    event.preventDefault();
    onSizeChange(clampKeyboardResize(size, increase ? 'increase' : 'decrease'));
  }

  // Content that fills the panel edge to edge; the rest is padded.
  const fullBleed = (view: SidePanelView | null | undefined): boolean =>
    view === webSessionView ||
    view === terminalSessionView ||
    view === 'new-tab' ||
    view === 'files' ||
    view === 'desktop' ||
    view === 'cloud-browser' ||
    view === 'ports' ||
    view === 'results' ||
    view?.startsWith('artifact:') === true ||
    workbench.panels.some((panel) => panel.view === view && usesNativePluginPanel(panel)) ||
    view === 'review' ||
    view === 'pull-request' ||
    view === 'subagent' ||
    view === 'chat';
  /**
   * Where a view shows: alone, the whole panel (`alone`, its classes then); split, laid over the
   * cell of its tab. Views stay where they are in the tree, so splitting remounts none.
   */
  function viewSlot(view: SidePanelView | null | undefined, alone: string): TabSlot {
    const tab = shownTabs.find((candidate) => candidate.view === view);
    return tab === undefined ? { className: 'hidden' } : tabSlot(tab, alone);
  }
  /** Where a tab shows (of a view open in several tabs that each render, an app's). */
  function tabSlot(tab: PanelTab, alone: string): TabSlot {
    if (!shownTabs.some((candidate) => candidate.id === tab.id)) return { className: 'hidden' };
    if (!splitShown) return { className: alone };
    const rect = splitRects[tab.id];
    return {
      className: `absolute z-10 ${fullBleed(tab.view) ? 'overflow-hidden' : 'overflow-y-auto px-4 pt-1 pb-4'}`,
      style: rect ?? { display: 'none' },
      'data-panel-split-target': tab.id,
      ...(tab.id === tabs.activeId ? {} : { onPointerDownCapture: () => selectTab(tab.id) }),
    };
  }
  /** Where a surface placing its own tabs shows (see `surfaceSlotClass`). */
  function surfaceSlot(
    view: SidePanelView | null | undefined,
    owns?: (tabId: string) => boolean,
  ): { className: string } {
    return {
      className: surfaceSlotClass({
        shownTabs,
        view,
        split: splitShown,
        ...(owns === undefined ? {} : { owns }),
      }),
    };
  }
  // The tabs shown with the active one, in the split.
  const shownTabIds = new Set(splitShown ? splitTabIds.filter((id) => id !== tabs.activeId) : []);
  const fixedContentWidth = manualResize;
  const toolEntries = panelToolEntries({
    items: panelItems,
    openViews,
    browserSessionView: nativeReady ? webSessionView : undefined,
    terminalSessionView,
    cloud: cloudRuntime !== null,
    // The personal workspace is no repository: no Review or Pull request (D5c). It is a folder
    // all the same, where Terminal opens a shell.
    repository: workspace !== null && workspace !== personalWorkspaceRoot,
    shell: workspace !== null,
    localComputer: isElectronDesktop() && isLocalMachine(),
    localPorts: cloudRuntime === null && (localClient !== null || previewPanel),
    // A cloud conversation's browser is the sandbox's, unless Desktop's own is at hand too.
    hiddenViews:
      cloudRuntime !== null && !(nativeReady && isElectronDesktop()) && webSessionView !== undefined
        ? [webSessionView]
        : [],
  });

  /** The sandbox's browser, or why it is not there yet. */
  function cloudBrowser(visible: boolean): ReactNode {
    return cloudRuntime !== null ? (
      <CloudBrowserView cloudRuntime={cloudRuntime} visible={visible} />
    ) : cloudWorkspaceStarting || cloudWorkspaceError !== null ? (
      <WorkspaceUnavailable
        cloudWorkspaceStarting={cloudWorkspaceStarting}
        cloudRuntime={cloudRuntime}
        cloudClientReady={cloudClient !== null}
        cloudWorkspacePhase={cloudWorkspacePhase}
        cloudWorkspaceError={cloudWorkspaceError}
        workspace={workspace}
        icon={Globe}
      />
    ) : (
      <PanelEmpty
        icon={Globe}
        title="No cloud browser"
        text="A conversation running in Crowdex Cloud shows its sandbox's browser here."
      />
    );
  }
  /** Replaces the "New tab" launcher with the tool picked from it. */
  function launchTool(entry: PanelToolEntry): void {
    replaceLauncher(() => {
      if (!entry.newInstance) onSelectView(entry.item.id);
      else if (entry.item.id === webSessionView) addBrowserTab();
      else if (entry.item.id === terminalSessionView) addTerminalTab();
    });
  }
  const panelTitle = panelTitleSlot.useElement();
  const panelTabList = panelTabListSlot.useElement();
  const tabBar = (
    <PanelTabBar
      panels={stripInstances}
      activeId={tabs.activeId}
      shownIds={shownTabIds}
      tabScrollRef={tabScrollRef}
      tabDragProps={tabDragProps}
      maximized={maximized}
      reviewCount={changedFiles}
      interactionLocked={false}
      onSelectTab={openPanelInstance}
      onCloseTab={closePanelInstance}
      onNewTab={() => onSelectView('new-tab')}
      onToggleMaximize={onToggleMaximize}
      onClose={onClose}
    />
  );

  return (
    <aside
      ref={asideRef}
      aria-hidden={hidden}
      data-resizing={resizing}
      inert={hidden || undefined}
      className={`desktop-side-panel relative isolate flex min-h-0 min-w-0 shrink-0 flex-col bg-background ${hidden ? 'pointer-events-none border-l-0' : presentation === 'screen' ? '!w-full' : 'max-sm:!w-full border-hairline-strong border-l'}`}
      style={
        manualResize
          ? {
              width: hidden ? '0%' : `${size}%`,
              // Docked beside the conversation (never over it, D2): the conversation keeps
              // its minimum width, its composer whole, however narrow the window.
              ...(hidden || maximized
                ? { minWidth: 0 }
                : {
                    minWidth: `min(${PANEL_MIN_PIXELS}px, ${DOCKED_PANEL_MAX_WIDTH})`,
                    maxWidth: DOCKED_PANEL_MAX_WIDTH,
                  }),
              marginLeft: 'auto',
            }
          : { height: '100%', width: hidden ? '0%' : '100%', marginLeft: 'auto' }
      }
    >
      {/* A maximized panel has no edge to drag. */}
      {manualResize && !maximized && (
        <hr
          aria-label="Resize panel"
          aria-disabled={false}
          aria-orientation="vertical"
          aria-valuemax={90}
          aria-valuemin={20}
          aria-valuenow={Math.round(size)}
          className="group -left-1.5 absolute inset-y-0 z-40 m-0 h-auto w-3 cursor-col-resize border-0 bg-transparent outline-none after:absolute after:top-1/2 after:left-1/2 after:h-10 after:w-1 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-border after:transition-colors hover:after:bg-primary focus-visible:after:bg-primary max-sm:hidden"
          onKeyDown={resizeWithKeyboard}
          onPointerDown={startResize}
          tabIndex={0}
        />
      )}
      <div
        ref={panelContentRef}
        className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-clip ${fixedContentWidth ? 'shrink-0 max-sm:!w-full' : ''}`}
        style={
          fixedContentWidth
            ? {
                // Laid out at the open width even while the aside animates or
                // is closed, so opening/closing only moves the clipping edge
                // instead of re-flowing the whole panel on every frame.
                width: maximized
                  ? 'calc(100cqw - 1px)'
                  : `calc(min(max(min(${PANEL_MIN_PIXELS}px, 100cqw), ${size}cqw), ${DOCKED_PANEL_MAX_WIDTH}) - 1px)`,
              }
            : undefined
        }
      >
        {presentation === 'screen' ? (
          <>
            {/* A phone's screen names the tab in its header and lists the tabs in a sheet. */}
            {panelTitle !== null &&
              createPortal(
                stripInstances.find((panel) => panel.id === tabs.activeId)?.title ?? 'Panel',
                panelTitle,
              )}
            {panelTabList !== null &&
              createPortal(
                <PanelTabList
                  activeId={tabs.activeId}
                  onAllApps={() => router.push(pluginsPath('browse'))}
                  onCloseTab={closePanelInstance}
                  onLaunch={launchTool}
                  onSelectTab={openPanelInstance}
                  panels={stripInstances}
                  reviewCount={changedFiles}
                  tools={toolEntries}
                />,
                panelTabList,
              )}
          </>
        ) : (
          tabBar
        )}
        <div
          className={`relative min-h-0 flex-1 ${
            splitShown || fullBleed(activeView) ? '' : 'overflow-y-auto px-4 pt-1 pb-4'
          }`}
          data-panel-split-target={splitShown || tabs.activeId === null ? undefined : tabs.activeId}
          ref={viewsRef}
        >
          {splitShown && split !== null && (
            <PanelSplitFrame containerRef={viewsRef} onRect={reportSplitRect} split={split} />
          )}
          {shown('new-tab') && (
            <div {...viewSlot('new-tab', 'contents')}>
              <NewTabView
                artifacts={results.artifacts}
                onAllApps={() => router.push(pluginsPath('browse'))}
                tools={toolEntries}
                onLaunch={launchTool}
                onOpenUrl={
                  nativeReady && webSessionView !== undefined
                    ? (address) =>
                        replaceLauncher(() => addBrowserTab('shared', resolveBrowserInput(address)))
                    : undefined
                }
                onOpenView={(view) => replaceLauncher(() => onSelectView(view))}
              />
            </div>
          )}
          {shown('results') && (
            <div {...viewSlot('results', 'contents')}>
              <div className="h-full overflow-y-auto p-4">
                <AppArtifactsSection
                  {...(UUID.test(taskId) ? { conversation: taskId } : {})}
                  empty={
                    <p className="p-4 text-center text-muted-foreground text-sm">
                      {results.loading
                        ? 'Loading artifacts…'
                        : (results.error ?? 'Files this conversation creates appear here.')}
                    </p>
                  }
                  onOpen={(artifact) => onSelectView(`artifact:${artifact.id}`)}
                />
              </div>
            </div>
          )}
          {openViews
            .filter((view) => view.startsWith('artifact:'))
            .map((view) => {
              // Made here, or docked here from another conversation (the account's index).
              const artifact =
                results.artifacts.find((candidate) => `artifact:${candidate.id}` === view) ??
                accountArtifacts.artifacts.find((candidate) => `artifact:${candidate.id}` === view);
              const loading = results.loading || accountArtifacts.loading;
              return (
                <div key={`${taskId}:${view}`} {...viewSlot(view, 'h-full')}>
                  {artifact ? (
                    <ArtifactItemView
                      artifact={artifact}
                      onClose={() => {
                        for (const tab of tabs.tabs.filter((entry) => entry.view === view))
                          closeTab(tab.id);
                      }}
                      onOpen={(target) => {
                        const next = results.artifacts.find(
                          (candidate) =>
                            candidate.space === target.space && candidate.path === target.path,
                        );
                        if (next !== undefined) onSelectView(`artifact:${next.id}`);
                      }}
                      placement="panel"
                      shownConversation={UUID.test(taskId) ? taskId : null}
                    />
                  ) : (
                    <p className="p-4 text-sm text-muted-foreground">
                      {loading
                        ? 'Loading artifact…'
                        : (results.error ?? 'This artifact is no longer available.')}
                    </p>
                  )}
                </div>
              );
            })}
          {workbench.panels
            .filter((panel) => usesNativePluginPanel(panel) && openViews.includes(panel.view))
            // An app shows in each of its tabs (one per item shown); a surface places its own tabs.
            .flatMap((panel): { panel: PluginPanel; tab: PanelTab | undefined }[] =>
              panel.appView !== undefined
                ? tabs.tabs.filter((tab) => tab.view === panel.view).map((tab) => ({ panel, tab }))
                : [{ panel, tab: undefined }],
            )
            .map(({ panel, tab }) => (
              <div
                key={tab?.id ?? panel.view}
                {...(tab !== undefined
                  ? tabSlot(tab, 'h-full')
                  : panel.view === webSessionView
                    ? surfaceSlot(panel.view)
                    : panel.view === terminalSessionView
                      ? surfaceSlot(panel.view, (id) => !agentTerminalTabIds.has(id))
                      : viewSlot(panel.view, 'h-full'))}
              >
                {sandboxBrowser && panel.view === webSessionView ? (
                  cloudBrowser(!hidden && shown(panel.view))
                ) : (
                  <NativePluginPanel
                    panel={panel}
                    tabId={tab?.id}
                    // Of an app's tabs shown side by side, the one shown last is its context.
                    current={tab === undefined || lastPanelTabOf(tabs, tab.view)?.id === tab.id}
                    {...(tab === undefined ? {} : { onTitleChange: renameAppTab(tab.id) })}
                    context={{
                      refreshWorkspace: onPullRequestChange,
                      currentBranch,
                      preview: pullRequestCockpitOverride !== undefined,
                      openPanel: onSelectView,
                      annotateSelection: onAnnotateSelection,
                      workspace,
                      client,
                      cloudRuntime,
                      taskId,
                      workspaceReady,
                      maximized,
                      visible:
                        !hidden &&
                        (tab === undefined
                          ? shown(panel.view)
                          : shownTabs.some((candidate) => candidate.id === tab.id)),
                      conversation: pluginConversation,
                      permissions: pluginPermissions,
                      services: new PluginHostServices()
                        .provide(webSurface, {
                          sessions: browserSessions,
                          visible: !hidden && shown(panel.view),
                          activeBrowserId,
                          placements: placementsOf(browserSessions.map((session) => session.id)),
                          controlledBrowserIds,
                          workspace,
                          onBrowserSelection,
                          onRenameSession: (id: string, name: string) =>
                            updateBrowserSession(id, (item) => ({ ...item, name })),
                          onSessionFavicon: (id: string, favicon: string | null) =>
                            updateBrowserSession(id, (item) => {
                              const { favicon: _previous, ...rest } = item;
                              return favicon === null ? rest : { ...rest, favicon };
                            }),
                          onSessionModeChange: (id: string, mode: BrowserSessionMode) =>
                            updateBrowserSession(id, (item) => withBrowserSessionMode(item, mode)),
                          onSelectSession: selectTab,
                          onNewSession: () => addBrowserTab(),
                          onCloseSession: closeTab,
                        })
                        .provide(
                          terminalSurface,
                          client && workspace
                            ? {
                                sessions: terminalSessions,
                                placements: placementsOf(
                                  terminalSessions.map((session) => session.id),
                                ),
                                onSelectSession: selectTab,
                                client,
                                workspace,
                                onAskSelectionInSideChat,
                                onMoreDetailsSelection,
                                onAnnotateSelection,
                                onRenameSession: renameTerminalSession,
                              }
                            : undefined,
                        ),
                    }}
                  />
                )}
              </div>
            ))}
          {!workspaceReady &&
          reviewDiffOverride === null &&
          pullRequestCockpitOverride === undefined &&
          activeView !== null &&
          activeView !== webSessionView &&
          activeView !== 'chat' &&
          activeView !== 'desktop' &&
          activeView !== 'computer' &&
          activeView !== 'cloud-browser' &&
          activeView !== 'plan' &&
          activeView !== 'pull-request' &&
          activeView !== 'subagent' &&
          activeItem !== undefined ? (
            <div {...viewSlot(activeView, 'contents')}>
              <WorkspaceUnavailable
                cloudWorkspaceStarting={cloudWorkspaceStarting}
                cloudRuntime={cloudRuntime}
                cloudClientReady={cloudClient !== null}
                cloudWorkspacePhase={cloudWorkspacePhase}
                cloudWorkspaceError={cloudWorkspaceError}
                workspace={workspace}
                icon={activeItem.icon}
              />
            </div>
          ) : null}
          {shown('cloud-browser') && (
            <div {...viewSlot('cloud-browser', 'contents')}>{cloudBrowser(!hidden)}</div>
          )}
          {shown('ports') && (workspaceReady || previewPanel) && (
            <div {...viewSlot('ports', 'contents')}>
              {cloudRuntime === null ? (
                <LocalPortsView
                  onOpen={(port) => {
                    const url = `http://localhost:${port}`;
                    if (nativeReady && webSessionView !== undefined) addBrowserTab('shared', url);
                    else window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  ports={previewPanel ? PREVIEW_LOCAL_PORTS : localPorts}
                />
              ) : (
                <PortsView ports={sandboxPorts} />
              )}
            </div>
          )}
          {shown('computer') && (
            <div {...viewSlot('computer', 'contents')}>
              <ComputerView />
            </div>
          )}
          {shown('desktop') && (
            <div {...viewSlot('desktop', 'contents')}>
              {cloudRuntime !== null ? (
                <DesktopDisplayView cloudRuntime={cloudRuntime} visible={!hidden} />
              ) : cloudWorkspaceStarting || cloudWorkspaceError !== null ? (
                <WorkspaceUnavailable
                  cloudWorkspaceStarting={cloudWorkspaceStarting}
                  cloudRuntime={cloudRuntime}
                  cloudClientReady={cloudClient !== null}
                  cloudWorkspacePhase={cloudWorkspacePhase}
                  cloudWorkspaceError={cloudWorkspaceError}
                  workspace={workspace}
                  icon={Monitor}
                />
              ) : (
                <PanelEmpty
                  icon={Monitor}
                  title="No cloud desktop"
                  text="A conversation running in Crowdex Cloud shows its sandbox's desktop here."
                />
              )}
            </div>
          )}
          {shown('plan') && (
            <div {...viewSlot('plan', 'contents')}>
              <PlanView
                onAnnotateSelection={onAnnotateSelection}
                onChangePlan={pluginConversation.sendMessage}
                plan={plan}
              />
            </div>
          )}
          {shown('review') &&
            (workspaceReady ||
              reviewDiffOverride !== null ||
              pullRequestCockpitOverride !== undefined) && (
              <div {...viewSlot('review', 'contents')}>
                <ReviewView
                  columns={!compact}
                  generateGitText={generateGitText}
                  onOpenPullRequest={() => onSelectView('pull-request')}
                  onPullRequestChange={onPullRequestChange}
                  reviewDiffOverride={reviewDiffOverride}
                  reviewError={reviewError}
                  reviewSource={reviewSource}
                  statusLines={statusLines}
                  diffText={diffText}
                  reviewLabel={reviewLabel}
                  pullRequest={pullRequest}
                  pullRequestCockpit={pullRequestCockpit}
                  onMutatePullRequest={mutateCurrentPullRequest}
                  onSubmitReview={onSubmitReview}
                  onToggleSource={() =>
                    setReviewSource((current) =>
                      current === 'changes' ? 'pull-request' : 'changes',
                    )
                  }
                  onFileAction={(path, action) => updateGitFile(path, action)}
                />
              </div>
            )}
          {shown('pull-request') && (
            <div {...viewSlot('pull-request', 'contents')}>
              <PullRequestPanel
                cloud={cloudRuntime !== null}
                onAttach={(issue) =>
                  onAnnotateSelection({
                    anchor: { x: window.innerWidth - 360, y: 160 },
                    content: pullRequestIssueText(issue),
                    sourceId: issue.key,
                    sourceKind: 'pull_request',
                    sourceLabel: 'Pull request',
                  })
                }
                onOpenReview={() => onSelectView('review')}
                onRefresh={onPullRequestChange}
                onSendMessage={(text) => pluginConversation.sendMessage(text)}
                previewCockpit={pullRequestCockpitOverride}
                taskId={taskId}
                visible={!hidden}
                workspace={workspace}
              />
            </div>
          )}
          {shown('subagent') && selectedSubagent !== null && (
            <div {...viewSlot('subagent', 'contents')}>
              <div className="desktop-panel-view-enter h-full">
                <DesktopSubagentConversation data={selectedSubagent} />
              </div>
            </div>
          )}
          <div {...surfaceSlot(terminalSessionView, (id) => agentTerminalTabIds.has(id))}>
            {openAgentTerminals.map((terminal) => {
              const placement = placementOf(terminal.id);
              return (
                <PlacedTab
                  id={terminal.id}
                  key={terminal.id}
                  onSelect={selectTab}
                  placement={placement}
                >
                  <AgentTerminalPane
                    terminal={terminal}
                    visible={placement !== undefined}
                    onKill={onKillAgentTerminal}
                    onStopped={onAgentTerminalStopped}
                  />
                </PlacedTab>
              );
            })}
          </div>
          {shown('files') && (workspaceReady || previewPanel) && (
            <div {...viewSlot('files', 'contents')}>
              <FilesView
                workspace={workspace}
                activeFile={activeFile}
                showFileSource={showFileSource}
                onToggleFileSource={() => setShowFileSource((value) => !value)}
                fileFilter={fileFilter}
                onFileFilterChange={setFileFilter}
                onClearFileFilter={() => setFileFilter('')}
                filesError={filesError}
                fileEntries={fileEntries}
                expandedDirectories={expandedDirectories}
                loadingDirectories={loadingDirectories}
                onRefreshFiles={() => void refreshFiles()}
                onToggleDirectory={(path) => void toggleDirectory(path)}
                onOpenFile={(path) => void openFile(path)}
                onSelectText={setFileSelection}
                onMentionFile={(path) => insertInComposer(`@${path} `, taskId)}
                editor={
                  editorOpener === undefined ||
                  workspace === null ||
                  (!isLocalMachine() && pullRequestCockpitOverride === undefined)
                    ? undefined
                    : {
                        name: editorOpener.name,
                        open: (path) =>
                          pullRequestCockpitOverride !== undefined ||
                          void openNativeWorkspaceIn(`${workspace}/${path}`, editorOpener.id).catch(
                            (error: unknown) =>
                              toast.error(
                                `Couldn’t open it in ${editorOpener.name}`,
                                errorMessage(error),
                              ),
                          ),
                      }
                }
              />
            </div>
          )}
          {openViews.includes('chat') && (
            <div {...viewSlot('chat', 'h-full')}>
              <SideChatPanel
                agent={agent}
                cloudRuntime={cloudRuntime}
                onSendToAgent={pluginConversation.sendMessage}
                prompt={sideChatPrompt}
                workspace={workspace}
                resolveReference={resolveReference}
              />
            </div>
          )}
        </div>
      </div>
      {splitPreview !== null && (
        <div
          className="pointer-events-none fixed z-drag rounded-lg border-2 border-primary shadow-desktop-float"
          style={{
            background: 'color-mix(in srgb, var(--primary) 20%, transparent)',
            height: splitPreview.height,
            left: splitPreview.left,
            top: splitPreview.top,
            width: splitPreview.width,
          }}
        />
      )}
      {fileSelection !== null && activeFile !== null && (
        <FileSelectionToolbar
          selection={fileSelection}
          filePath={activeFile.path}
          onAnnotateSelection={onAnnotateSelection}
          onMoreDetailsSelection={onMoreDetailsSelection}
          onAskSelectionInSideChat={onAskSelectionInSideChat}
          onDismiss={dismissFileSelection}
        />
      )}
    </aside>
  );
}
